import {CREATED} from 'http-status-codes';
import _ from 'lodash';
import {
    CatalogProductStatus,
    ProductAttributesField,
    ProductData,
    ProductField,
    ProductInCatalog,
} from '@treez/commons/sharedTypings/product';
import React, {FunctionComponent, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import SwipeableDrawer, {SwipeableDrawerProps} from '@material-ui/core/SwipeableDrawer';
import {ModalType} from '@treez-ui/core/feedback/modals';
import {PriceTier} from '@treez/commons/sharedTypings/priceTier';
import {getProductDiff, returnNewAttributes} from '~/lib/product';
import {updateCatalogUniqueValues} from '~/redux/actions/ProductMetaDataActions';
import {
    addNewProduct,
    clearOverride,
    patchProduct,
    searchCatalogForProducts,
} from '~/utils/ProductAPI';
import ProductForm, {ProductFormProps} from './ProductForm';
import {selectProductMetaData} from '~/redux/selectors/ProductMetaDataSelectors';
import {
    selectOrganizationPriceTiersForSelection,
    selectStoresInOrganization,
} from '~/redux/selectors/organizationSelectors';
import {GlobalApplicationState} from '~/redux/reducers/RootReducer';
import {StoreAttributes} from '~/lib/directory';
import {
    useProductFormContext,
    useProductFormStateDispatch,
} from '~/contexts/ProductForm/productFormContext';
import {closeProductFormOnly, openProductFormOnly} from '~/contexts/ProductForm/productFormActions';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {StoreProductValidationResult} from '~/lib/errors';
import ProductValidationErrorMessage from '../widgets/products/ProductValidationErrorMessage';

const {INGREDIENT, FLAVOR, GENERAL, INTERNAL, EFFECT} = ProductAttributesField;

export interface ProductInputDataOptions {
    flavorAttributesOptions: string[];
    generalAttributesOptions: string[];
    effectAttributesOptions: string[];
    ingredientAttributesOptions: string[];
    internalAttributesOptions: string[];
    brandOptions: string[];
}

export enum CatalogType {
    BRAND_CATALOG = 'BRAND_CATALOG',
    CENTRAL_CATALOG = 'CENTRAL_CATALOG',
    STORE_CATALOG = 'STORE_CATALOG',
}

interface Props
    extends Omit<SwipeableDrawerProps, 'onOpen' | 'onClose' | 'open'>,
        Omit<
            ProductFormProps,
            | 'productDataOptions'
            | 'addProduct'
            | 'handleCancel'
            | 'editProduct'
            | 'productToEdit'
            | 'handleOpenProductValidationFeedback'
        > {
    onCloseForm?: (productAdded?: Partial<ProductInCatalog>) => void;
    onOpenForm?: (productToEdit?: Partial<ProductInCatalog>) => void;
}

export const ProductFormDrawer: FunctionComponent<Props> = (props: Props) => {
    const {catalogId, onCloseForm, onOpenForm, catalogType} = props;

    const dispatch = useDispatch();
    const productDataOptions = useSelector(selectProductMetaData);

    const {productToInspect, isProductFormOpen, scrollTo} = useProductFormContext();
    const productFormDispatch = useProductFormStateDispatch();

    const {
        flavorAttributesOptions,
        generalAttributesOptions,
        effectAttributesOptions,
        ingredientAttributesOptions,
        internalAttributesOptions,
        brandOptions,
    } = productDataOptions;

    const stores = useSelector<GlobalApplicationState, StoreAttributes[] | undefined>(
        selectStoresInOrganization
    );

    const priceTiers = useSelector<GlobalApplicationState, PriceTier[] | undefined>(
        selectOrganizationPriceTiersForSelection
    );

    const updateMetaDataWithNewlyAddedUniqueValues = (
        productToCheck: Partial<ProductInCatalog>
    ) => {
        const {attributes = {}, brandName = ''} = productToCheck;

        const flavorsAdded = _.get(attributes, FLAVOR, []) as string[];
        const effectsAdded = _.get(attributes, EFFECT, []) as string[];
        const ingredientsAdded = _.get(attributes, INGREDIENT, []) as string[];
        const generalAttributesAdded = _.get(attributes, GENERAL, []) as string[];
        const internalTagsAdded = _.get(attributes, INTERNAL, []) as string[];

        const brandAlreadyExistedInCatalog = _.includes(brandOptions, brandName);

        const newFlavors = returnNewAttributes(flavorsAdded, flavorAttributesOptions);

        const newEffects = returnNewAttributes(effectsAdded, effectAttributesOptions);

        const newIngredients = returnNewAttributes(ingredientsAdded, ingredientAttributesOptions);

        const newGeneralAttributes = returnNewAttributes(
            generalAttributesAdded,
            generalAttributesOptions
        );

        const newInternalTags = returnNewAttributes(internalTagsAdded, internalAttributesOptions);

        const noNewFlavors = _.size(newFlavors) === 0;
        const noNewEffects = _.size(newEffects) === 0;
        const noNewIngredients = _.size(newIngredients) === 0;
        const noNewGeneralAttributes = _.size(newGeneralAttributes) === 0;
        const noNewInternalTags = _.size(newInternalTags) === 0;

        const noNewMetaDataAdded =
            brandAlreadyExistedInCatalog &&
            noNewFlavors &&
            noNewEffects &&
            noNewIngredients &&
            noNewGeneralAttributes &&
            noNewInternalTags;

        if (noNewMetaDataAdded) {
        } else {
            dispatch(
                updateCatalogUniqueValues({
                    brandName: brandAlreadyExistedInCatalog
                        ? brandOptions
                        : [...brandOptions, brandName],
                    effect: noNewEffects
                        ? effectAttributesOptions
                        : _.concat(effectAttributesOptions, newEffects),
                    flavor: noNewFlavors
                        ? flavorAttributesOptions
                        : _.concat(flavorAttributesOptions, newFlavors),
                    ingredient: noNewIngredients
                        ? ingredientAttributesOptions
                        : _.concat(ingredientAttributesOptions, newIngredients),
                    general: noNewGeneralAttributes
                        ? generalAttributesOptions
                        : _.concat(generalAttributesOptions, newGeneralAttributes),
                    internal: noNewInternalTags
                        ? internalAttributesOptions
                        : _.concat(internalAttributesOptions, newInternalTags),
                })
            );
        }
    };

    const closeForm = (productAdded?: Partial<ProductInCatalog>) => {
        productFormDispatch(closeProductFormOnly());
        onCloseForm?.(productAdded);
    };

    const handleCancel = (draftProduct: Partial<ProductInCatalog>) => {
        if (_.isEqual(productToInspect, draftProduct)) {
            closeForm();
        } else {
            openFeedbackModal(
                () => {
                    closeFeedbackModal();
                    closeForm();
                },
                'Product Information Will Be Lost',
                {
                    type: ModalType.WARNING,
                    bodyText:
                        'If you exit now, the product information you have entered will be lost',
                    cancelOption: true,
                    onCancel: closeFeedbackModal,
                }
            );
        }
    };

    const addProduct = async (productToAdd: Partial<ProductInCatalog>) => {
        if (productToAdd == null) {
        } else {
            try {
                const response = await addNewProduct(productToAdd, catalogId);

                if (response.status !== CREATED) {
                    throw Error('product was not created');
                }
                updateMetaDataWithNewlyAddedUniqueValues(productToAdd);

                const newProduct = response.data.data;
                closeForm(newProduct);
            } catch (error) {
                console.error(error);
                openFeedbackModal(closeFeedbackModal, 'Error Adding Product', {
                    type: ModalType.ERROR,
                    bodyText: `There was an error adding the product:
                            ${error.message}`,
                });
            }
        }
    };

    const handleClearOverrides = async (
        overridesToClear: ProductData[],
        draftProduct: Partial<ProductInCatalog>
    ) => {
        if (_.isEmpty(overridesToClear)) {
            return Promise.resolve();
        }

        try {
            await clearOverride(Number(draftProduct.id), catalogId, overridesToClear);

            return await Promise.resolve();
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, 'Error Resetting Field', {
                type: ModalType.ERROR,
                bodyText: `There was an error resetting field:
                        ${error.message}`,
            });

            return Promise.reject();
        }
    };

    const editProduct = async (
        editedProduct: Partial<ProductInCatalog>,
        overridesToClear: ProductData[]
    ) => {
        const {id} = editedProduct;
        if (productToInspect == null || editedProduct == null || id == null) {
        } else {
            const productDiff = getProductDiff(productToInspect, editedProduct);

            try {
                await patchProduct(id, productDiff, catalogId);

                await handleClearOverrides(overridesToClear, editedProduct);

                const filters = [
                    {
                        field: ProductField.ID,
                        fuzzy: false,
                        values: [id],
                    },
                ];

                // the search API, will return only products that are ACTIVE by default if there is no status supplied
                // supplying both of these statuses, will make sure that even if the product that was edited is DEACTIVATED it is returned in the search.
                const status = [CatalogProductStatus.ACTIVE, CatalogProductStatus.DEACTIVATED];

                const {data: products} = await searchCatalogForProducts(catalogId, {
                    excludeLinkedProduct: false,
                    filters,
                    status,
                });

                if (products.length > 0) {
                    const updatedProduct = products[0];
                    closeForm(updatedProduct);
                }
            } catch (error) {
                console.error(error);
                openFeedbackModal(closeFeedbackModal, 'Error Editing Product', {
                    type: ModalType.ERROR,
                    bodyText: `There was an error editing the product:
                            ${error.message}`,
                });
            }
        }
    };

    const handleOpenProductValidationFeedback = (
        storeProductValidationErrorResult: StoreProductValidationResult[]
    ) => {
        openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
            type: ModalType.ERROR,
            bodyElement: (
                <>
                    {storeProductValidationErrorResult.map((validationResult, key) => (
                        <ProductValidationErrorMessage
                            key={key}
                            productValidationResult={validationResult.productValidationResult}
                            region={validationResult.region}
                            store={validationResult.store}
                        />
                    ))}
                </>
            ),
        });
    };

    useEffect(() => {
        if (isProductFormOpen) {
            onOpenForm?.(productToInspect);
        }
    }, [productToInspect, isProductFormOpen]);

    const swipeableDrawerProps = _.omit(props, [
        'addProduct',
        'catalogId',
        'catalogType',
        'onCloseForm',
        'onOpenForm',
        'editProduct',
        'handleCancel',
        'productDataOptions',
        'productToEdit',
    ]);

    return (
        <SwipeableDrawer
            onClose={() => productFormDispatch(closeProductFormOnly())}
            onOpen={() =>
                productFormDispatch(openProductFormOnly(productToInspect as ProductInCatalog))
            }
            open={isProductFormOpen}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            {...swipeableDrawerProps}
        >
            {isProductFormOpen && (
                <ProductForm
                    addProduct={addProduct}
                    catalogId={catalogId}
                    catalogType={catalogType}
                    editProduct={editProduct}
                    handleCancel={handleCancel}
                    handleOpenProductValidationFeedback={handleOpenProductValidationFeedback}
                    priceTiers={priceTiers}
                    productDataOptions={productDataOptions}
                    productToEdit={productToInspect}
                    scrollTo={scrollTo}
                    stores={stores}
                />
            )}
        </SwipeableDrawer>
    );
};

export default ProductFormDrawer;
